/** @format */

import { memo } from 'react'
import { CopyBlock, ocean } from 'react-code-blocks'

import styles from 'dir/styles/courses.module.css'

export const _4 = memo(() => (
  <section className={styles.section}>
    <h2 className={styles.h2}>4 - Редиректы</h2>
    <p className={styles.description}>
      Перенаправления это удобный инструмент навигации пользователя между маршрутами приложения. Для их реализации
      используется компонент <code className={styles.code}>{`<Redirect>`}</code> или методы{' '}
      <code className={styles.code}>history.push()</code> и <code className={styles.code}>history.replace()</code>.{' '}
    </p>
    <section className={styles.subsection}>
      <h3 className={styles.h3}>4.1 - Проп history</h3>
      <p className={styles.description}>
        Важно знать раличие методов <code className={styles.code}>push</code> и{' '}
        <code className={styles.code}>replace</code>.{' '}
      </p>
      <ul className={styles.list}>
        <li className={styles.list__item}>
          <code className={styles.code}>history.push()</code> - добавит новую запись в журнал истории, пользователь
          может вернуться на тот маршрут с которого пришел.
        </li>
        <li className={styles.list__item}>
          <code className={styles.code}>history.replace()</code> - перезапишет текущую запись в журнале истории затерев
          текущую, пользователь не сможет вернутся на маршрут с которого пришел.
        </li>
      </ul>
      <p className={styles.description}>
        Продолжим работать со статьями. При рендере раута <code className={styles.code}>/articles</code> есть категории
        статей, но при первом рендере маршрута еще нет строки запроса с параметром{' '}
        <code className={styles.code}>category</code>. Именно поэтому необходимо сделать редирект, при этом перезаписав
        текущую страницу истории, чтобы пользователь не мог вернуться на страницу без параметров запроса.{' '}
      </p>
      <CopyBlock
        text={`componentDidMount() {
  /*
  * При маунте компонента проверяем наличие параметра \`category\` в строке запроса.
  */
  const category = getCategoryFromProps(this.props);

  /*
  * Если параметра нет, делаем \`history.replace\` с текущим маршрутом,
  * но добавляем категорию \`all\`, чтобы забрать все статьи с бекенда.
  * При этом перезаписываем текущую страницу истории.
  */
  if (!category) {
    this.props.history.replace({
      pathname: this.props.location.pathname,
      search: \`category=all\`,
    });

    return;
  }

  /*
  * Если при рендере такой параметр уже есть, например пользователь зашел по
  * сохраненной ссылке, делаем HTTP-запрос за статьями.
  */
  fetchArticlesByCategory(category).then(articles =>
    this.setState({ articles }),
  );
}`}
        language="javascript"
        showLineNumbers={false}
        theme={ocean}
      />
    </section>
    <section className={styles.subsection}>
      <h3 className={styles.h3}>4.2 - Свойство location.state</h3>
      <p className={styles.description}>
        Позволяет передавать кастомные данные между маршрутами. Например список статей это ссылки, при клике в ссылку
        переходим на новый маршрут - страницу статьи.{' '}
      </p>
      <CopyBlock
        text={`https://app.com/articles/:articleId`}
        language="javascript"
        showLineNumbers={false}
        theme={ocean}
      />
      <p className={styles.description}>
        Тогда, если пользователь пришел с <code className={styles.code}>https://app.com/articles?category=sports</code>,
        и захочет вернуться на сраницу всех статей, нажав стрелку "Обратно", он попадет куда надо. Но что если
        необходимо реализовать кнопку "Обратно к статьям" в интерфейсе приложения, как узнать откуда мы пришли?{' '}
      </p>
      <p className={styles.description}>
        Каждую статью сделаем ссылкой, добавив в свойство <code className={styles.code}>state</code> информацию о
        текущем маршруте.{' '}
      </p>
      <CopyBlock
        text={`<Link
  to={{
    pathname: \`/articles/$ {id}\`,
    state: { from: this.props.location },
  }}
>
  Article title
</Link>`}
        language="javascript"
        showLineNumbers={false}
        theme={ocean}
      />
      <p className={styles.description}>
        Тогда в компоненте статьи можем получить эту информацию и при клике на кнопку "Обратно к статьям" перенаправить
        пользователя на предыдущий маршрут.{' '}
      </p>
      <CopyBlock
        text={`handleGoBack = () => {
  this.props.history.push(this.props.location.state.from);
};`}
        language="javascript"
        showLineNumbers={false}
        theme={ocean}
      />
      <p className={styles.description}>
        Нужно учитывать ситуацию когда пользователь перешел на страницу статьи по сохраненный ссылке, и в текущей сессии
        не был на странице всех статей. В свойстве <code className={styles.code}>location.state</code> не будет объекта,
        и попытка доступа к <code className={styles.code}>state.from</code> приведет к ошибке выполнения скрипта.{' '}
      </p>
      <p className={styles.description}>
        Поэтому необходимо проверить <code className={styles.code}>location.state</code>. Если{' '}
        <code className={styles.code}>state</code> есть - перенаправляем пользователя туда, откуда он пришел. Если{' '}
        <code className={styles.code}>state</code> нет - перенаправляем на{' '}
        <code className={styles.code}>/articles</code>, при этом параметр <code className={styles.code}>category</code>{' '}
        назначаем по свойству статьи (если в объекте статьи есть такое свойство).{' '}
      </p>
      <CopyBlock
        text={`handleGoBack = () => {
  const { state } = this.props.location;
  const { category } = this.state;

  if (state) {
    this.props.history.push(state.from);
    return;
  }

  this.props.history.push({
    pathname: '/articles',
    search: \`?category=$ {category}\`,
  });
};`}
        language="javascript"
        showLineNumbers={false}
        theme={ocean}
      />
    </section>
  </section>
))
